import { defineStore } from 'pinia'
import { computed, reactive } from 'vue'

import carDraftsApi from '@/api/database/car-drafts-api'
import carsApi from '@/api/database/cars-api'
import companiesApi from '@/api/database/companies-api'
import type { Table } from '@/types/supabase'

type CacheEntry = {
  cache: Map<string, string>
  bulkFetch: (ids: string[]) => Promise<Map<string, string>>
}

export const useBreadcrumbsStore = defineStore('breadcrumbs', () => {
  const registry = reactive(new Map<Table, CacheEntry>([
    ['cars', {
      cache: new Map<string, string>(),
      bulkFetch: carsApi.fetchCarTitleByIds
    }],
    ['car_drafts', {
      cache: new Map<string, string>(),
      bulkFetch: carDraftsApi.fetchCarDraftTitleByIds
    }],
    ['companies', {
      cache: new Map<string, string>(),
      bulkFetch: companiesApi.fetchCompanyNameByIds
    }]
  ]))


  const tables = computed(() => Array.from(registry.keys()))

  const fetchCached = async (table: Table, id: string): Promise<string> => {
    const entry = registry.get(table)
    if (!entry) throw new Error(`Unknown cache key: ${table}`)

    if (entry.cache.has(id)) {
      return entry.cache.get(id) as string
    }

    const results = await entry.bulkFetch([id])
    const value = results.get(id)
    if (value) {
      entry.cache.set(id, value)
    }
    return value ?? id
  }

  const repopulateTableCache = async (table: Table): Promise<void> => {
    const entry = registry.get(table)
    if (!entry) return

    const ids = Array.from(entry.cache.keys())
    entry.cache.clear() // Clear existing cache
    const items = await entry.bulkFetch(ids)
    for (const [id, title] of Object.entries(items)) {
      entry.cache.set(id, title)
    }
  }

  const fetchCarTitle = (carId: string): Promise<string> => fetchCached('cars', carId)
  const fetchDraftTitle = (carDraftId: string): Promise<string> => fetchCached('car_drafts', carDraftId)
  const fetchCompanyName = (companyId: string): Promise<string> => fetchCached('companies', companyId)

  return {
    tables,
    repopulateTableCache,
    fetchCarTitle,
    fetchDraftTitle,
    fetchCompanyName
  }
})
